import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { getElementScrollValueFixed } from "../scrollbar/scrollbar-utils";

@Component({
    tag: "ux-scrollpanel",
    styleUrl: "scrollpanel.scss",
    shadow: true
})
export class UxScrollpanel {
    @Element() host: HTMLUxScrollpanelElement;

    /** Set to `true` if the scrollbars should display additional controls */
    @Prop() readonly controls: boolean = false;

    /** Controls the scroll direction */
    @Prop() readonly direction: "auto" | "horizontal" | "vertical" = "auto";

    /** Controls the scroll direction */
    @Prop({ reflect: true, mutable: true }) scrollbars: "horizontal" | "vertical" | "both" | "none" = "none";

    /** Native scroll behavior */
    @Prop() readonly scrollBehavior: ScrollBehavior = "smooth";

    /** Local state */
    @State() isActive = false;
    @State() needsVerticalScrollbar = false;
    @State() needsHorizontalScrollbar = false;

    /** Emitted when the scrollbar was dragged */
    @Event() uxScroll: EventEmitter<{ horizontal: number; vertical: number }>;

    /** Update UI when the viewport is resized */
    @Listen("resize", { target: "window" })
    handleResize() {
        this.updateDebounced();
    }

    @Watch("needsVerticalScrollbar")
    @Watch("needsHorizontalScrollbar")
    activateScrollbars() {
        if (this.needsHorizontalScrollbar && this.needsVerticalScrollbar) {
            this.scrollbars = "both";
        } else if (this.needsHorizontalScrollbar) {
            this.scrollbars = "horizontal";
        } else if (this.needsVerticalScrollbar) {
            this.scrollbars = "vertical";
        }
        this.updateScrollbarsDebounced();
    }

    @Watch("isActive")
    handleFocus() {
        if (!this.isActive && document.activeElement === this.host) {
            this.host.blur();
        }
    }

    async componentWillLoad() {
        this.update();
    }

    async componentDidLoad() {
        this.updateDebounced();
    }

    private getContent = (): HTMLElement | null => {
        return this.host.shadowRoot?.querySelector(".content") || null;
    };

    private getScrollbars = (): HTMLUxScrollbarElement[] => {
        return Array.from(this.host.shadowRoot?.querySelectorAll("ux-scrollbar") || []);
    };

    /** Update handle size and position */
    private update = () => {
        const bounds = this.host.getBoundingClientRect();
        const content = this.getContent();
        if (content) {
            this.needsVerticalScrollbar = content.scrollHeight - bounds.height > 0;
            this.needsHorizontalScrollbar = content.scrollWidth - bounds.width > 0;
        } else {
            this.needsVerticalScrollbar = false;
            this.needsHorizontalScrollbar = false;
        }
    };

    /** Debounced instance of `update()` */
    private updateDebounced = debounce(this.update, 50);

    /** Update scrollbars */
    private updateScrollbars = () => {
        const content = this.getContent();
        if (content) {
            this.getScrollbars().forEach((scrollbar) => {
                scrollbar.connect(content);
            });
        }
    };

    /** Debounced instance of `updateScrollbars()` */
    private updateScrollbarsDebounced = debounce(this.updateScrollbars, 50);

    private activate = () => {
        this.isActive = true;
    };

    private deactivate = () => {
        this.isActive = false;
    };

    private handleContentScroll = () => {
        this.activate();
        const content = this.getContent() as HTMLElement;
        const scrollValue = getElementScrollValueFixed(content);
        this.uxScroll.emit({
            horizontal: scrollValue.left,
            vertical: scrollValue.top
        });
    };

    private handleScrollbarScroll = (event: Event) => {
        event.stopPropagation();
    };

    render() {
        return (
            <Host onMouseEnter={this.activate} onMouseLeave={this.deactivate}>
                <div class="content" onScroll={this.handleContentScroll}>
                    <slot onSlotchange={this.updateScrollbarsDebounced} />
                </div>
                <div
                    class={{
                        ["scrollbars"]: true,
                        ["inactive"]: !this.isActive
                    }}
                >
                    {this.needsVerticalScrollbar && (this.direction === "vertical" || this.direction === "auto") && (
                        <ux-scrollbar
                            direction="vertical"
                            controls={this.controls}
                            scrollBehavior={this.scrollBehavior}
                            onUxScroll={this.handleScrollbarScroll}
                        />
                    )}
                    {this.needsHorizontalScrollbar && (this.direction === "horizontal" || this.direction === "auto") && (
                        <ux-scrollbar
                            direction="horizontal"
                            controls={this.controls}
                            scrollBehavior={this.scrollBehavior}
                            onUxScroll={this.handleScrollbarScroll}
                        />
                    )}
                </div>
            </Host>
        );
    }
}
